import React, { useState, useEffect, useRef } from "react";
import "./Login.css";
import styles from "./Auth.module.css";
import { GoogleLogin } from "@react-oauth/google";
import { Eye, EyeOff } from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { authAPI } from "../../api/apiHelper";
import useToast from "../../hooks/useToast";
import Toast from "../../components/Toast";
import dec from "../../assets/landing/dec.svg";
import { Logo } from "../../components/logo/Logo";
import ReCAPTCHA from "react-google-recaptcha";

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast, success, error, hideToast } = useToast();

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeTlZQsAAAAADSV8zywpgg9tbFy5aX49r6FKbjY";

  // Restore remembered email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail && savedEmail.trim()) {
      setEmail(savedEmail.trim());
      setRemember(true);
    }
  }, []);

  // Check for verification status in URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verified = params.get("verified");
    const verifiedEmail = params.get("email");
    const errorParam = params.get("error");

    if (verified === "true") {
      success("✅ Email verified successfully! You can now log in.", {
        title: "Verification Successful",
      });
      // Pre-fill email if available
      if (verifiedEmail) {
        setEmail(decodeURIComponent(verifiedEmail));
      }
      // Clean URL
      navigate("/login", { replace: true });
    } else if (verified === "false") {
      if (errorParam === "expired") {
        error("Verification link has expired. Please request a new one.", {
          title: "Verification Failed",
        });
      } else {
        error("Email verification failed. Please try again.", {
          title: "Verification Failed",
        });
      }
      // Clean URL
      navigate("/login", { replace: true });
    }
  }, [location.search, navigate, success, error]);


  const handleEmailLogin = async (email, password) => {
    setIsLoading(true);
    try {
      const response = await authAPI.login(email, password, recaptchaToken);
      console.log("response: ", response);

      // Store user data and token
      localStorage.setItem("authToken", response.token);
      localStorage.setItem(
        "userData",
        JSON.stringify({
          name: response.user.name,
          email: response.user.email,
          isOrganizationCreated: response.user.isOrganization,
          id: response.user._id,
          role: response.user.role,
          teamType: response.user.teamType || null
        })
      );
      localStorage.setItem("orgId", response.user.organizationId);
      success("Login successful! Welcome back!", { title: "Success" });

      // Reset reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }

      // Check if user selected a plan before login
      const selectedPlan = sessionStorage.getItem("selectedPlan");
      console.log("Login - checking for selected plan:", selectedPlan);

      // Navigate based on organization status and selected plan
      if (response.user.isOrganization) {
        if (selectedPlan) {
          const planDetails = JSON.parse(selectedPlan);
          const billingUrl = `/home/settings/billing?planId=${planDetails.planId}&planName=${encodeURIComponent(planDetails.planName)}&pricePerUser=${planDetails.pricePerUser}&billingCycle=${planDetails.billingCycle}`;
          navigate(billingUrl);
        } else {
          navigate("/home");
        }
      } else {
        navigate("/onboarding");
      }
    } catch (err) {
      console.error("Login error:", err);

      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }

      // Check for email verification error
      if (err.message?.includes("verify your email") || err.message?.includes("email before logging")) {
        error("Please verify your email first. Check your inbox for verification link.", {
          title: "Email Not Verified"
        });
        // Store email in localStorage for verification page
        localStorage.setItem("userData", JSON.stringify({ email }));
        setTimeout(() => navigate("/verify-email-pending"), 2000);
        return;
      }

      if (err.response_code === 422) {
        error("User not found. Redirecting to Sign Up...", {
          title: "Account Not Found",
        });
        // Redirect to signup page after 1 seconds
        setTimeout(() => {
          navigate("/signup");
        }, 1000);
      } else if (err.response_code === 401) {
        error("Invalid password. Please try again.", { title: "Login Failed" });
      } else if (err.isNetworkError) {
        error("Network error. Please check your connection.", {
          title: "Connection Error",
        });
      } else {
        error(err.message || "Login failed. Please try again.", {
          title: "Login Failed",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const decoded = jwtDecode(credentialResponse.credential);

      console.log("Google user data:", decoded);

      const response = await authAPI.googleAuth(
        credentialResponse.credential,
        decoded.name,
        decoded.email,
        decoded.picture
      );

      // Store user data and token
      localStorage.setItem("authToken", response.token);
      localStorage.setItem(
        "userData",
        JSON.stringify({
          name: response.user.name,
          email: response.user.email,
          isOrganizationCreated: response.user.organization,
          id: response.user._id,
          role: response.user.role,
          teamType: response.user.teamType || null
        })
      );
      localStorage.setItem("orgId", response.user.organizationId);
      success("Google login successful!", { title: "Success" });

      // Check if user selected a plan before login
      const selectedPlan = sessionStorage.getItem("selectedPlan");
      console.log("Google Login - checking for selected plan:", selectedPlan);

      // Navigate based on organization status and selected plan
      if (response.user.organization) {
        if (selectedPlan) {
          const planDetails = JSON.parse(selectedPlan);
          const billingUrl = `/home/settings/billing?planId=${planDetails.planId}&planName=${encodeURIComponent(planDetails.planName)}&pricePerUser=${planDetails.pricePerUser}&billingCycle=${planDetails.billingCycle}`;
          navigate(billingUrl);
        } else {
          navigate("/home");
        }
      } else {
        navigate("/onboarding");
      }
    } catch (err) {
  console.error("Login error:", err);

  // Axios error with response from backend
  if (err.response) {
    const status = err.response.status;
    const message = err.response.data?.message;

    if (status === 422) {
      error(message || "User not found. Please check your email.", {
        title: "Login Failed",
      });
    } else if (status === 401) {
      error(message || "Invalid password. Please try again.", {
        title: "Login Failed",
      });
    } else {
      error(message || "Login failed. Please try again.", {
        title: "Login Failed",
      });
    }
  }
  // True network error (server down, no internet)
  else {
    error("Network error. Please check your connection.", {
      title: "Connection Error",
    });
  }
} finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (email) => {
    if (!email) {
      error("Please enter your email address.", { title: "Email Required" });
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.forgotPassword(email);
      success("Password reset email sent! Please check your inbox.", {
        title: "Email Sent",
      });
    } catch (err) {
      console.error("Forgot password error:", err);

      if (err.response_code === 404) {
        error("Email not found. Please check your email address.", {
          title: "Email Not Found",
        });
      } else if (err.isNetworkError) {
        error("Network error. Please check your connection.", {
          title: "Connection Error",
        });
      } else {
        error(err.message || "Failed to send reset email. Please try again.", {
          title: "Error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    if (!trimmedEmail || !trimmedPassword) {
      error("Please fill in all fields.", { title: "Validation Error" });
      return;
    }
    if (!recaptchaToken) {
      error("Please complete the reCAPTCHA verification by checking the 'I'm not a robot' box.", { title: "reCAPTCHA Verification Required" });
      return;
    }
    // Handle remember me
    if (remember) {
      localStorage.setItem("rememberedEmail", trimmedEmail);
    } else {
      localStorage.removeItem("rememberedEmail");
    }
    await handleEmailLogin(trimmedEmail, trimmedPassword);
  };

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  return (
    <div className="login-root">
      <Toast
        open={ toast.open }
        message={ toast.message }
        severity={ toast.severity }
        title={ toast.title }
        duration={ toast.duration }
        onClose={ hideToast }
        position={ toast.position }
      />
      <div className="login-left-panel">
        <NavLink to="/">
          <Logo
            variant="white authLogo"
            className={styles.logo}
          />
        </NavLink>
        <img src={ dec } alt="Decorative background shape" className="svg_style" />
        <img src={ dec } alt="Decorative background shape" className="svg_style_1" />
        <div className="login-features">
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Seamless Transcription</div>
              <div className="login-feature-desc">
                Effortlessly transcribe audio to text with our advanced AI
                technology
              </div>
            </div>
          </div>
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Multi-language Support</div>
              <div className="login-feature-desc">
                Transcribe conversations in multiple languages with ease
              </div>
            </div>
          </div>
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Collaboration Tools</div>
              <div className="login-feature-desc">
                Collaborate with team members by sharing and annotating
                transcripts in real-time
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="login-right-panel">
        <form className="login-form" onSubmit={ onSubmit }>
          <div className="login-title">Sign in</div>
          <div className="login-subtitle">
            Sign in to unlock the power of intelligent transcripts
          </div>
          <div
            style={ {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            } }
          >
            <GoogleLogin
              onSuccess={ handleGoogleLogin }
              onError={ () => {
                error("Google login failed. Please try again.", {
                  title: "Login Failed",
                });
              } }
              disabled={ isLoading }
              text="continue_with"
            />
          </div>
          <div className="login-divider-row">
            <div className="login-divider" />
            <span className="login-or">OR</span>
            <div className="login-divider" />
          </div>
          <div className="login-label">Email</div>
          <input
            className="login-input"
            type="email"
            placeholder="youremail@address.com"
            value={ email }
            onChange={ (e) => setEmail(e.target.value) }
            required
            disabled={ isLoading }
          />
          <div className="login-label">Password</div>
          <div style={ { position: "relative" } }>
            <input
              className="login-input"
              type={ showPassword ? "text" : "password" }
              placeholder="8+ characters required"
              value={ password }
              onChange={ (e) => setPassword(e.target.value) }
              required
              disabled={ isLoading }
            />
            <button
              type="button"
              onClick={ () => setShowPassword(!showPassword) }
              style={ {
                position: "absolute",
                right: "10px",
                top: "35%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0"
              } }
            >
              { showPassword ? <EyeOff size={ 20 } /> : <Eye size={ 20 } /> }
            </button>
          </div>
          <div className="login-row login-remember-row">
            <label className="login-remember-label">
              <input
                type="checkbox"
                checked={ remember }
                onChange={ (e) => {
                  setRemember(e.target.checked);
                  if (!e.target.checked) localStorage.removeItem("rememberedEmail");
                } }
                disabled={ isLoading }
              />
              Remember me
            </label>
            <button
              type="button"
              className="login-forgot"
              onClick={ () => handleForgotPassword(email) }
              disabled={ isLoading }
            >
              Forgot Password
            </button>
          </div>
          
          {/* reCAPTCHA */ }
          <div style={ { display: 'flex', justifyContent: 'center', marginBottom: '16px' } }>
            <ReCAPTCHA
              ref={ recaptchaRef }
              sitekey={ RECAPTCHA_SITE_KEY }
              onChange={ onRecaptchaChange }
              onExpired={ () => setRecaptchaToken(null) }
              onError={ () => setRecaptchaToken(null) }
            />
          </div>
          
          <button
            type="submit"
            className="login-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? "Signing in..." : "Sign in"}
          </button>
          <div className="login-signup-row">
            Don't have an account yet?{ " " }
            <NavLink to="/signup" className="login-signup-link">
              Sign up
            </NavLink>
          </div>
        </form>
      </div>
    </div>
  );
};
